import { useSuspenseQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { AlertBar } from "@/components/AlertBar";
import {
	DescriptionDetails,
	DescriptionList,
	DescriptionTerm,
} from "@/components/catalyst/DescriptionList";
import { Heading } from "@/components/catalyst/Heading";
import { Text } from "@/components/catalyst/Text";
import { orpc } from "@/orpc/client";
import type { CreateReferralLinkFormValues } from "../schema";

/** Step 3: Review all terms and confirm before creating. */
export function StepReview() {
	const { watch } = useFormContext<CreateReferralLinkFormValues>();
	const values = watch();

	const { data: partners } = useSuspenseQuery(
		orpc.partners.list.queryOptions({ input: {} }),
	);

	const partner = partners.find((p) => p.id === values.partnerId);

	const formatExpiry = (expiresAt: string | null | undefined) => {
		if (!expiresAt) return "Never";
		const date = new Date(expiresAt);
		return date.toLocaleDateString("en-GB", {
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<div>
			<Heading level={3}>Review & Confirm</Heading>
			<Text className="mt-1 mb-6">
				Double-check everything below. Once created, financial terms cannot be
				changed.
			</Text>

			<div className="rounded-lg border border-base-content/10 p-6">
				<DescriptionList>
					<DescriptionTerm>Partner</DescriptionTerm>
					<DescriptionDetails>{partner?.name ?? "—"}</DescriptionDetails>

					<DescriptionTerm>Code</DescriptionTerm>
					<DescriptionDetails>{values.slug || "—"}</DescriptionDetails>

					<DescriptionTerm>Plan</DescriptionTerm>
					<DescriptionDetails className="capitalize">
						{values.planHint || "Any"}
					</DescriptionDetails>

					<DescriptionTerm>Payout Rate</DescriptionTerm>
					<DescriptionDetails>
						{Number(values.payoutRateBps) === 0
							? "Tracking only (no payouts)"
							: `${values.payoutRateBps}%`}
					</DescriptionDetails>

					<DescriptionTerm>Minimum Payout</DescriptionTerm>
					<DescriptionDetails>
						{Number(values.payoutRateBps) === 0
							? "N/A"
							: `$${values.minPayoutDollars}`}
					</DescriptionDetails>

					<DescriptionTerm>Payout Duration</DescriptionTerm>
					<DescriptionDetails>
						{Number(values.payoutRateBps) === 0
							? "N/A"
							: values.payoutDurationDays
								? `${values.payoutDurationDays} days`
								: "Indefinite (lifetime)"}
					</DescriptionDetails>

					<DescriptionTerm>Max Redemptions</DescriptionTerm>
					<DescriptionDetails>
						{values.maxRedemptions ?? "Unlimited"}
					</DescriptionDetails>

					<DescriptionTerm>Expires</DescriptionTerm>
					<DescriptionDetails>
						{formatExpiry(values.expiresAt)}
					</DescriptionDetails>
				</DescriptionList>
			</div>

			<AlertBar
				variant="warning"
				title="Partner notification"
				icon={AlertTriangle}
				className="mt-6"
			>
				The partner will receive an email and must accept these terms before the
				link becomes active. These terms cannot be changed after creation.
			</AlertBar>
		</div>
	);
}
